import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { User, onAuthStateChanged, onIdTokenChanged, multiFactor, getMultiFactorResolver, type MultiFactorAssertion, type MultiFactorResolver } from "firebase/auth";
import { doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, authPersistenceReady, signInWithGoogle, signInWithEmail, signInWithMicrosoft, logOut, functions } from "../firebase";
import { db } from "../firebase";
import { authProbe } from "../lib/authProbe";
import { logger } from '../lib/logger';
import { InactivityWarning } from "../components/InactivityWarning";

interface AuthContextType {
  user: User | null;
  role: string | null;
  firmRole: string | null;
  isSuperAdmin: boolean;
  isPlatformOwner: boolean;
  platformRole: string | null;
  isMfaEnrolled: boolean;
  isMfaSessionVerified: boolean;
  isAuthReady: boolean;
  authError: string | null;
  /** Sprint 12.6 — true when the signed-in user has no firmId claim and no
   *  super-admin claim, signalling that App.tsx should redirect them to
   *  /onboarding/firm-bootstrap instead of dumping them out of the app. */
  needsFirmBootstrap: boolean;
  pendingMfaResolver: MultiFactorResolver | null;
  signIn: () => Promise<void>;
  signInWithEmailPassword: (email: string, password: string) => Promise<"success" | "mfa-required" | "error">;
  signInWithMicrosoftAuth: () => Promise<void>;
  signOut: () => Promise<void>;
  completeMfaSignIn: (assertion: MultiFactorAssertion) => Promise<void>;
  clearMfaChallenge: () => void;
  clearError: () => void;
  showInactivityWarning: boolean;
  extendSession: () => void;
  refreshMfaStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  firmRole: null,
  isSuperAdmin: false,
  isPlatformOwner: false,
  platformRole: null,
  isMfaEnrolled: false,
  isMfaSessionVerified: false,
  isAuthReady: false,
  authError: null,
  needsFirmBootstrap: false,
  pendingMfaResolver: null,
  signIn: async () => {},
  signInWithEmailPassword: async () => "error",
  signInWithMicrosoftAuth: async () => {},
  signOut: async () => {},
  completeMfaSignIn: async () => {},
  clearMfaChallenge: () => {},
  clearError: () => {},
  showInactivityWarning: false,
  extendSession: () => {},
  refreshMfaStatus: async () => {},
});

const DEFAULT_AUTO_LOCK_MINUTES = 30;
const IDLE_WARNING_MS = 30 * 60 * 1000;
const IDLE_SIGN_OUT_MS = 60 * 60 * 1000;
const ACTIVITY_BROADCAST_THROTTLE_MS = 10 * 1000;
const CLAIM_RETRY_DELAYS_MS = [500, 1500, 3000] as const;
const MISSING_CLAIM_GRACE_MS = 15 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"] as const;
const TOKEN_REFRESH_TIMEOUT_MS = 5000;
const USE_LOCAL_DOGFOOD_SIGN_IN =
  import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === "1" ||
  import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === "true";
const LOCAL_DOGFOOD_EMAIL = (import.meta.env.VITE_LOCAL_DOGFOOD_EMAIL as string | undefined)?.trim() ?? "";
const LOCAL_DOGFOOD_PASSWORD = (import.meta.env.VITE_LOCAL_DOGFOOD_PASSWORD as string | undefined)?.trim() ?? "";

function getActivityStorageKey(uid: string): string {
  return `iamvault:last-activity:${uid}`;
}

async function withTimeout<T>(work: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  return await Promise.race([
    work,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
    }),
  ]);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [firmRole, setFirmRole] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [platformRole, setPlatformRole] = useState<string | null>(null);
  const [isMfaEnrolled, setIsMfaEnrolled] = useState(false);
  const [isMfaSessionVerified, setIsMfaSessionVerified] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [needsFirmBootstrap, setNeedsFirmBootstrap] = useState<boolean>(false);
  const [pendingMfaResolver, setPendingMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [autoLockMinutes, setAutoLockMinutes] = useState(DEFAULT_AUTO_LOCK_MINUTES);

  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inactivitySignOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userRef = useRef<User | null>(null);
  const autoLockMinutesRef = useRef(DEFAULT_AUTO_LOCK_MINUTES);
  const lastActivityBroadcastRef = useRef(0);
  const lastClaimsRefreshAtRef = useRef(0);
  const lastActiveAtRef = useRef<number>(0);
  const firmIdClaimRef = useRef<string | null>(null);
  const roleClaimRef = useRef<string | null>(null);
  const missingClaimsSinceRef = useRef<number | null>(null);
  const localDogfoodSignInAttemptedRef = useRef(false);
  const lastOperatorLoginAuditRef = useRef<string | null>(null);

  const waitForClaimsRetry = (delayMs: number) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

  const clearInactivityTimers = useCallback(() => {
    if (inactivityWarningTimeoutRef.current) {
      clearTimeout(inactivityWarningTimeoutRef.current);
      inactivityWarningTimeoutRef.current = null;
    }
    if (inactivitySignOutTimeoutRef.current) {
      clearTimeout(inactivitySignOutTimeoutRef.current);
      inactivitySignOutTimeoutRef.current = null;
    }
  }, []);

  const recordOperatorAuthEvent = useCallback((eventType: string, metadata?: Record<string, unknown>) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const recordAudit = httpsCallable<{ eventType: string; metadata?: Record<string, unknown> }, { success: boolean }>(
      functions,
      "recordOperatorAuthAudit",
    );
    recordAudit({ eventType, metadata }).catch((error) => {
      logger.warn("[AuthContext] operator auth audit failed (non-fatal)", { eventType, error });
    });
  }, []);

  const signOutUser = useCallback(async () => {
    authProbe("auth:sign-out:start", { currentUserPresent: Boolean(auth.currentUser?.uid) });
    recordOperatorAuthEvent("operator_logout", { reason: "user_sign_out" });
    try {
      await logOut();
      authProbe("auth:sign-out:resolved", { currentUserPresent: Boolean(auth.currentUser?.uid) });
    } catch (error) {
      authProbe("auth:sign-out:error", { message: error instanceof Error ? error.message : String(error) });
      logger.error("Sign out error", { error });
    }
  }, [recordOperatorAuthEvent]);

  const scheduleInactivityTimers = useCallback((lastActivityAt: number) => {
    clearInactivityTimers();
    setShowInactivityWarning(false);

    const currentUser = userRef.current;
    if (!currentUser) return;

    const elapsedMs = Math.max(0, Date.now() - lastActivityAt);
    if (elapsedMs >= IDLE_SIGN_OUT_MS) {
      recordOperatorAuthEvent("operator_session_expired", { elapsedMs, reason: "idle_timeout" });
      void signOutUser();
      return;
    }

    const warningDelayMs = Math.max(0, IDLE_WARNING_MS - elapsedMs);
    const signOutDelayMs = Math.max(0, IDLE_SIGN_OUT_MS - elapsedMs);

    inactivityWarningTimeoutRef.current = setTimeout(() => {
      setShowInactivityWarning(true);
    }, warningDelayMs);
    inactivitySignOutTimeoutRef.current = setTimeout(() => {
      recordOperatorAuthEvent("operator_session_expired", { reason: "idle_timeout" });
      void signOutUser();
    }, signOutDelayMs);
  }, [clearInactivityTimers, recordOperatorAuthEvent, signOutUser]);

  const broadcastActivity = useCallback((force = false) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    const now = Date.now();
    if (!force && now - lastActivityBroadcastRef.current < ACTIVITY_BROADCAST_THROTTLE_MS) {
      return;
    }

    lastActivityBroadcastRef.current = now;
    lastActiveAtRef.current = now;
    window.localStorage.setItem(getActivityStorageKey(currentUser.uid), String(now));
    scheduleInactivityTimers(now);
  }, [scheduleInactivityTimers]);

  const extendSession = useCallback(() => {
    setShowInactivityWarning(false);
    broadcastActivity(true);
  }, [broadcastActivity]);

  const refreshMfaStatus = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsMfaEnrolled(false);
      setIsMfaSessionVerified(false);
      return;
    }
    await currentUser.reload();
    setIsMfaEnrolled(multiFactor(currentUser).enrolledFactors.length > 0);
    const idTokenResult = await currentUser.getIdTokenResult(true);
    const firebaseClaim = (idTokenResult.claims.firebase ?? {}) as Record<string, unknown>;
    const signInSecondFactor =
      typeof firebaseClaim.sign_in_second_factor === "string" ? firebaseClaim.sign_in_second_factor.trim() : "";
    setIsMfaSessionVerified(signInSecondFactor.length > 0 || idTokenResult.claims.mfaVerified === true);
  }, []);

  const syncClaimsFromToken = useCallback(async (currentUser: User, forceRefresh = false) => {
    let idTokenResult = await currentUser.getIdTokenResult(forceRefresh);
    let hasFirmId = !!idTokenResult.claims.firmId;
    let isSuperAdminUser = idTokenResult.claims.superAdmin === true;
    const firebaseClaim = (idTokenResult.claims.firebase ?? {}) as Record<string, unknown>;
    const signInSecondFactor =
      typeof firebaseClaim.sign_in_second_factor === "string" ? firebaseClaim.sign_in_second_factor.trim() : "";
    const sessionMfaVerified = signInSecondFactor.length > 0 || idTokenResult.claims.mfaVerified === true;

    if (!hasFirmId && !isSuperAdminUser) {
      const missingSince = missingClaimsSinceRef.current ?? Date.now();
      missingClaimsSinceRef.current = missingSince;

      for (const delayMs of CLAIM_RETRY_DELAYS_MS) {
        await waitForClaimsRetry(delayMs);
        idTokenResult = await currentUser.getIdTokenResult(true);
        hasFirmId = !!idTokenResult.claims.firmId;
        isSuperAdminUser = idTokenResult.claims.superAdmin === true;
        if (hasFirmId || isSuperAdminUser) break;
      }

      if (!hasFirmId && !isSuperAdminUser && Date.now() - missingSince < MISSING_CLAIM_GRACE_MS) {
        setNeedsFirmBootstrap(false);
        setRole((current) => current ?? "user");
        setIsMfaEnrolled(multiFactor(currentUser).enrolledFactors.length > 0);
        setIsMfaSessionVerified(sessionMfaVerified);
        return true;
      }
    }

    const firmIdClaim = (idTokenResult.claims.firmId as string | undefined) ?? null;
    const roleClaim = (idTokenResult.claims.role as string | undefined) ?? null;

    // Sprint 12.6 — invite-only gate: users without a firmId claim are now
    // routed to /onboarding/firm-bootstrap (where they can create a firm or
    // redeem an invite) instead of being silently signed out. Super-admins
    // remain exempt.
    if (!hasFirmId && !isSuperAdminUser) {
      setNeedsFirmBootstrap(true);
      setRole("user");
      setFirmRole(null);
      setIsSuperAdmin(false);
      setIsPlatformOwner(false);
      firmIdClaimRef.current = null;
      roleClaimRef.current = null;
      return true;
    }
    setNeedsFirmBootstrap(false);
    missingClaimsSinceRef.current = null;

    const userRole = idTokenResult.claims.admin ? "admin" : "user";
    setRole(userRole);
    setFirmRole(roleClaim);
    setIsSuperAdmin(isSuperAdminUser);
    setIsPlatformOwner(idTokenResult.claims.isPlatformOwner === true);
    // Wave S / S-1 — operator RBAC role claim (separate from firm-side role).
    const nextPlatformRole =
      typeof idTokenResult.claims.platformRole === "string"
        ? idTokenResult.claims.platformRole
        : idTokenResult.claims.isPlatformOwner === true || isSuperAdminUser
          ? "platform_owner"
          : null;
    setPlatformRole(nextPlatformRole);
    if (nextPlatformRole && lastOperatorLoginAuditRef.current !== currentUser.uid) {
      lastOperatorLoginAuditRef.current = currentUser.uid;
      recordOperatorAuthEvent("operator_login", {
        platformRole: nextPlatformRole,
        mfaVerified: sessionMfaVerified,
      });
    }
    setIsMfaEnrolled(multiFactor(currentUser).enrolledFactors.length > 0);
    setIsMfaSessionVerified(sessionMfaVerified);
    firmIdClaimRef.current = firmIdClaim;
    roleClaimRef.current = roleClaim;
    return true;
  }, [recordOperatorAuthEvent, signOutUser]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const clearMfaChallenge = useCallback(() => {
    setPendingMfaResolver(null);
  }, []);

  useEffect(() => {
    autoLockMinutesRef.current = autoLockMinutes;
  }, [autoLockMinutes]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      authProbe("auth:onAuthStateChanged", {
        currentUserPresent: Boolean(currentUser?.uid),
        currentUserUidPresent: Boolean(currentUser?.uid),
        authCurrentUserPresent: Boolean(auth.currentUser?.uid),
        appName: auth.app.name,
      });
      if (currentUser) {
        setAuthError(null);
        setUser(currentUser);
        setPendingMfaResolver(null);

        // FIX 1: Write lastSignInTime to Firestore so Team Performance page can display it
        updateDoc(doc(db, "users", currentUser.uid), {
          lastSignInTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).catch((err) => { logger.warn("[AuthContext] lastSignInTime write failed (non-fatal)", { err }); });

        // Start each new auth session from a fresh activity timestamp.
        // Without this reset, a stale value from a prior session can trigger
        // an immediate auto-lock sign-out right after successful login.
        const now = Date.now();
        lastActiveAtRef.current = now;
        window.localStorage.setItem(getActivityStorageKey(currentUser.uid), String(now));

        try {
          // Do not block the session gate indefinitely on a forced token refresh.
          // Hosted App Check failures can throttle refresh requests for an entire day.
          try {
            await withTimeout(currentUser.getIdToken(true), TOKEN_REFRESH_TIMEOUT_MS, "auth token refresh");
            authProbe("auth:getIdToken:resolved", { currentUserUidPresent: Boolean(currentUser.uid), mode: "force-refresh" });
          } catch (refreshError) {
            authProbe("auth:getIdToken:soft-fail", {
              message: refreshError instanceof Error ? refreshError.message : String(refreshError),
              currentUserUidPresent: Boolean(currentUser.uid),
            });
            logger.warn("Force token refresh failed during auth bootstrap, falling back to cached claims", { error: refreshError });
          }

          const allowed = await withTimeout(
            syncClaimsFromToken(currentUser, false),
            TOKEN_REFRESH_TIMEOUT_MS,
            "claim sync"
          );
          authProbe("auth:claims:resolved", { allowed });
          if (!allowed) {
            return;
          }
        } catch (error) {
          authProbe("auth:getIdToken:error", { message: error instanceof Error ? error.message : String(error) });
          logger.error("Error fetching user role", { error });
          setRole("user");
          setIsMfaEnrolled(false);
          setIsMfaSessionVerified(false);
        }

        // Start token refresh interval (every 55 minutes)
        if (tokenRefreshIntervalRef.current) {
          clearInterval(tokenRefreshIntervalRef.current);
        }
        tokenRefreshIntervalRef.current = setInterval(async () => {
          try {
            await syncClaimsFromToken(currentUser, true);
            logger.info("Auth token refreshed");
          } catch (error) {
            logger.error("Token refresh failed", { error });
          }
        }, 55 * 60 * 1000);

      } else {
        setUser(null);
        setRole(null);
        setFirmRole(null);
        setIsSuperAdmin(false);
        setIsPlatformOwner(false);
        setIsMfaEnrolled(false);
        setIsMfaSessionVerified(false);
        setPendingMfaResolver(null);
        setAutoLockMinutes(DEFAULT_AUTO_LOCK_MINUTES);
        lastActivityBroadcastRef.current = 0;
        lastClaimsRefreshAtRef.current = 0;
        lastActiveAtRef.current = Date.now();
        firmIdClaimRef.current = null;
        roleClaimRef.current = null;
        missingClaimsSinceRef.current = null;
        lastOperatorLoginAuditRef.current = null;

        // Clear token refresh interval
        if (tokenRefreshIntervalRef.current) {
          clearInterval(tokenRefreshIntervalRef.current);
          tokenRefreshIntervalRef.current = null;
        }

        // Clear inactivity timers
        clearInactivityTimers();
        setShowInactivityWarning(false);
      }
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, [clearInactivityTimers, syncClaimsFromToken]);

  // DELTA-024: react to refreshed ID tokens and immediately sync changed firm/role claims.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      authProbe("auth:onIdTokenChanged", {
        currentUserPresent: Boolean(currentUser?.uid),
        authCurrentUserPresent: Boolean(auth.currentUser?.uid),
      });
      if (!currentUser) return;
      try {
        const idTokenResult = await currentUser.getIdTokenResult(false);
        const nextFirmId = (idTokenResult.claims.firmId as string | undefined) ?? null;
        const nextRole = (idTokenResult.claims.role as string | undefined) ?? null;
        const claimsChanged = nextFirmId !== firmIdClaimRef.current || nextRole !== roleClaimRef.current;
        if (claimsChanged) {
          await syncClaimsFromToken(currentUser, true);
        }
      } catch (error) {
        logger.warn("onIdTokenChanged claim sync failed", { error });
      }
    });

    return () => unsubscribe();
  }, [syncClaimsFromToken]);

  useEffect(() => {
    if (!user?.uid) return;

    // Subscribe to user document with error handling
    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        const snapshotData = snapshot.exists() ? snapshot.data() : null;
        const minutes = snapshotData && typeof snapshotData.autoLockMinutes === "number" && snapshotData.autoLockMinutes >= 10
          ? snapshotData.autoLockMinutes
          : DEFAULT_AUTO_LOCK_MINUTES;
        setAutoLockMinutes(minutes);

        const refreshAt =
          snapshotData &&
          snapshotData.claimsRefreshAt &&
          typeof snapshotData.claimsRefreshAt.toMillis === "function"
            ? snapshotData.claimsRefreshAt.toMillis()
            : 0;

        if (refreshAt > lastClaimsRefreshAtRef.current && userRef.current) {
          lastClaimsRefreshAtRef.current = refreshAt;
          void syncClaimsFromToken(userRef.current, true).catch((error) => {
            logger.warn("Claims refresh sync failed", { error });
          });
        }

        // Treat active Firestore snapshot traffic as session activity.
        const now = Date.now();
        lastActiveAtRef.current = now;
        scheduleInactivityTimers(now);
      },
      (error) => {
        // Log Firestore error but don't block auth — use defaults
        logger.warn("Firestore user doc snapshot error (using defaults)", { error });
        setAutoLockMinutes(DEFAULT_AUTO_LOCK_MINUTES);
      }
    );

    return () => unsubscribe();
  }, [syncClaimsFromToken, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const storageKey = getActivityStorageKey(user.uid);
    const now = Date.now();
    const savedValue = Number(window.localStorage.getItem(storageKey));
    const totalMs = IDLE_SIGN_OUT_MS;
    const isValidSavedValue = Number.isFinite(savedValue) && savedValue > 0;

    // If saved activity is older than the active timeout window, treat this
    // as a new session baseline instead of forcing immediate sign-out.
    const initialActivityAt =
      isValidSavedValue && now - savedValue < totalMs
        ? savedValue
        : now;

    if (!isValidSavedValue || initialActivityAt === now) {
      window.localStorage.setItem(storageKey, String(initialActivityAt));
    }

    lastActiveAtRef.current = initialActivityAt;
    scheduleInactivityTimers(initialActivityAt);
  }, [autoLockMinutes, scheduleInactivityTimers, user?.uid]);

  const signIn = async () => {
    setAuthError(null);
    setPendingMfaResolver(null);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      logger.error("Sign in error", { error });
      // Silently ignore popup-closed-by-user since it's expected when user cancels
      if (error.code === "auth/popup-closed-by-user") {
        return;
      }
      if (error.code === "auth/multi-factor-auth-required") {
        setPendingMfaResolver(getMultiFactorResolver(auth, error));
        recordOperatorAuthEvent("operator_mfa_challenge", { provider: "google" });
      } else if (error.code === "auth/popup-blocked") {
        setAuthError("Sign-in popup was blocked. Please click the 'Sign in' button again, or check your browser's address bar to 'Always allow popups' for this site.");
      } else if (error.code === "auth/cancelled-popup-request") {
        // Ignore user cancellation
      } else {
        setAuthError(error.message || "An error occurred during sign in.");
      }
    }
  };

  const signInWithEmailPassword = async (email: string, password: string) => {
    setAuthError(null);
    setPendingMfaResolver(null);
    authProbe("auth:email-sign-in:start", {
      appName: auth.app.name,
      authCurrentUserPresent: Boolean(auth.currentUser?.uid),
    });
    await authPersistenceReady;
    authProbe("auth:persistence:ready", {
      appName: auth.app.name,
      authCurrentUserPresent: Boolean(auth.currentUser?.uid),
    });
    try {
      await signInWithEmail(email, password);
      authProbe("auth:email-sign-in:resolved", {
        authCurrentUserPresent: Boolean(auth.currentUser?.uid),
        authCurrentUserUidPresent: Boolean(auth.currentUser?.uid),
      });
      return "success" as const;
    } catch (error: any) {
      authProbe("auth:email-sign-in:rejected", {
        code: typeof error?.code === "string" ? error.code : null,
        message: error instanceof Error ? error.message : String(error),
        authCurrentUserPresent: Boolean(auth.currentUser?.uid),
      });
      logger.error("Email sign-in error", { error });
      if (error.code === "auth/multi-factor-auth-required") {
        setPendingMfaResolver(getMultiFactorResolver(auth, error));
        recordOperatorAuthEvent("operator_mfa_challenge", { provider: "password" });
        return "mfa-required" as const;
      } else if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential" ||
        error.code === "auth/invalid-password"
      ) {
        setAuthError("Email or password is incorrect. Please try again.");
      } else if (error.code === "auth/user-not-found" || error.code === "auth/invalid-email") {
        setAuthError("No account found with this email address.");
      } else if (error.code === "auth/too-many-requests") {
        setAuthError("Too many failed attempts. Please reset your password or try again later.");
      } else if (error.code === "auth/user-disabled") {
        setAuthError("This account has been disabled. Please contact support.");
      } else {
        setAuthError(error.message || "Sign in failed. Please try again.");
      }
      return "error" as const;
    }
  };

  const signInWithMicrosoftAuth = async () => {
    setAuthError(null);
    setPendingMfaResolver(null);
    try {
      await signInWithMicrosoft();
    } catch (error: any) {
      logger.error("Microsoft sign-in error", { error });
      if (error.code === "auth/multi-factor-auth-required") {
        setPendingMfaResolver(getMultiFactorResolver(auth, error));
        recordOperatorAuthEvent("operator_mfa_challenge", { provider: "microsoft" });
      } else if (error.code === "auth/popup-blocked") {
        setAuthError("Sign-in popup was blocked. Please allow popups for this site and try again.");
      } else if (
        error.code === "auth/cancelled-popup-request" ||
        error.code === "auth/popup-closed-by-user"
      ) {
        // User cancelled — no error shown
      } else {
        setAuthError(error.message || "Microsoft sign in failed. Please try again.");
      }
    }
  };

  const completeMfaSignIn = useCallback(async (assertion: MultiFactorAssertion) => {
    if (!pendingMfaResolver) {
      throw new Error("No multi-factor sign-in is pending.");
    }

    setAuthError(null);
    try {
      await pendingMfaResolver.resolveSignIn(assertion);
      setPendingMfaResolver(null);
    } catch (error: any) {
      logger.error("MFA sign-in challenge failed", { error });
      recordOperatorAuthEvent("operator_mfa_failed", { message: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }, [pendingMfaResolver, recordOperatorAuthEvent]);

  const clearError = () => setAuthError(null);

  useEffect(() => {
    if (!USE_LOCAL_DOGFOOD_SIGN_IN || !LOCAL_DOGFOOD_EMAIL || !LOCAL_DOGFOOD_PASSWORD) return;
    if (user || pendingMfaResolver || localDogfoodSignInAttemptedRef.current) return;

    localDogfoodSignInAttemptedRef.current = true;
    void signInWithEmailPassword(LOCAL_DOGFOOD_EMAIL, LOCAL_DOGFOOD_PASSWORD).then((result) => {
      if (result === "error") {
        logger.warn("Local dogfood auto sign-in failed", { email: LOCAL_DOGFOOD_EMAIL });
      }
    });
  }, [pendingMfaResolver, user]);

  // Track user activity for inactivity timeout
  useEffect(() => {
    if (!user) return;
    const handleActivity = () => {
      broadcastActivity();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [broadcastActivity, user]);

  useEffect(() => {
    if (!user?.uid) return;

    const storageKey = getActivityStorageKey(user.uid);
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !event.newValue) return;

      const lastActivityAt = Number(event.newValue);
      if (!Number.isFinite(lastActivityAt) || lastActivityAt <= 0) return;
      scheduleInactivityTimers(lastActivityAt);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [scheduleInactivityTimers, user?.uid]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
      clearInactivityTimers();
    };
  }, [clearInactivityTimers]);

  const warningLeadMinutes = Math.round((IDLE_SIGN_OUT_MS - IDLE_WARNING_MS) / 60000);

  return (
    <AuthContext.Provider value={{ user, role, firmRole, isSuperAdmin, isPlatformOwner, platformRole, isMfaEnrolled, isMfaSessionVerified, isAuthReady, authError, needsFirmBootstrap, pendingMfaResolver, signIn, signInWithEmailPassword, signInWithMicrosoftAuth, signOut: signOutUser, completeMfaSignIn, clearMfaChallenge, clearError, showInactivityWarning, extendSession, refreshMfaStatus }}>
      {children}
      
      {showInactivityWarning && (
        <InactivityWarning
          warningLeadMinutes={warningLeadMinutes}
          onExtend={extendSession}
          onSignOut={signOutUser}
        />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
